import { useQuery } from "@tanstack/react-query";
import { getCmmsUploadLimits } from "../fetchers/cmmsUploadLimits";

// Server default (CmmsUploadOptions.MaxBytes). Used only until the query resolves, and if
// the request fails: a guard that blocks everything while limits are unknown would be worse
// than one that occasionally lets an oversized file through to a server-side rejection.
export const FALLBACK_MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * Upload limits as enforced by the API. Rarely changes, so it is cached for the session.
 */
export function useCmmsUploadLimits() {
  const query = useQuery(["cmmsUploadLimits"], getCmmsUploadLimits, {
    staleTime: Infinity,
    retry: 1,
  });

  return {
    maxBytes: query.data?.maxBytes ?? FALLBACK_MAX_UPLOAD_BYTES,
    allowedContentTypes: query.data?.allowedContentTypes ?? null,
    isLoading: query.isLoading,
  };
}

/**
 * Client-side pre-flight check. The server validates independently — this only turns a
 * round-trip and an error code into immediate feedback.
 *
 * Returns null when acceptable, or "size" / "type" naming what is wrong.
 */
export function checkUploadAgainstLimits(file, { maxBytes, allowedContentTypes }) {
  if (!file) return null;
  if (maxBytes && file.size > maxBytes) return "size";

  // A null list means the limits have not loaded yet — do not guess at the type.
  if (allowedContentTypes && allowedContentTypes.length > 0) {
    const mediaType = (file.type ?? "").split(";")[0].trim().toLowerCase();
    if (!allowedContentTypes.some((allowed) => allowed.toLowerCase() === mediaType)) return "type";
  }

  return null;
}
